import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Prompt, useHistory } from 'react-router-dom'
import {
  makeStyles, Checkbox, FormControlLabel, Tabs, Tab, Snackbar, Chip, TextField
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { Alert } from '@material-ui/lab'
import { useQuery } from '@tanstack/react-query'
import AdminPage from '../../../admin/shared/AdminPage'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import SydLabel from '../../../../commonDesign/SydLabel'
import Loading from '../../../../molecules/Loading'
import {
  useGetMeetingConfigEffective,
  useAdminSetMeetingConfig
} from '../../../../../api/meetings'
import { fetchUsers } from '../../../../../service'

/* Mirror these with
   data-svc-core-data-api/apps/coreData/application/meetings/domain/meetingBot/botConfigSchemas.js
   — duplicated to avoid pulling the backend package into the UI bundle. */
const MAX_AVATAR_BYTES = 1300000
const MAX_BOT_NAME = 100
const MAX_DISCLOSURE = 60
const MAX_CHAT_MESSAGE = 4096
const BOT_NAME_TOKEN = '{{botName}}'

const KEY = {
  NAME: 'ai.meeting.bot.name',
  DISCLOSURE_ENABLED: 'ai.meeting.bot.disclosure.enabled',
  DISCLOSURE_TEXT: 'ai.meeting.bot.disclosure.text',
  AVATAR: 'ai.meeting.bot.avatarAssetRef',
  ON_BOT_JOIN_ENABLED: 'ai.meeting.bot.chat.onBotJoin.enabled',
  ON_BOT_JOIN_MESSAGE: 'ai.meeting.bot.chat.onBotJoin.message',
  ON_PART_JOIN_ENABLED: 'ai.meeting.bot.chat.onParticipantJoin.enabled',
  ON_PART_JOIN_MESSAGE: 'ai.meeting.bot.chat.onParticipantJoin.message',
  ON_PART_JOIN_EXCLUDE_HOST: 'ai.meeting.bot.chat.onParticipantJoin.excludeHost'
}

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 'var(--padding-md)'
  },
  title: {
    fontSize: 'var(--text-size-lg)',
    fontWeight: 600,
    color: 'var(--color-neutral-800)'
  },
  subtitle: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-500)',
    marginTop: '2px'
  },
  tabsBar: {
    borderBottom: '1px solid var(--color-neutral-200)',
    marginBottom: 'var(--padding-md)'
  },
  userPickerRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--padding-md)',
    marginBottom: 'var(--padding-md)'
  },
  card: {
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-md)',
    padding: 'var(--padding-md)',
    marginBottom: 'var(--padding-md)'
  },
  cardHeading: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: 'var(--padding-sm)'
  },
  fieldRow: {
    marginBottom: 'var(--padding-md)'
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4
  },
  fieldLabel: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 500,
    color: 'var(--color-neutral-800)'
  },
  sourceChip: {
    height: 18,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  sourceDefault: { backgroundColor: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)' },
  sourceFirm: { backgroundColor: 'var(--color-info-050, #eff6ff)', color: 'var(--color-info-700, #1d4ed8)' },
  sourceUser: { backgroundColor: 'var(--color-primary-050, #f5f3ff)', color: 'var(--color-primary-700, #6d28d9)' },
  hint: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)',
    marginTop: 4
  },
  textArea: {
    width: '100%',
    minHeight: 80,
    padding: '8px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'inherit',
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-900)',
    resize: 'vertical'
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--padding-md)'
  },
  avatarPreview: {
    width: 96,
    height: 96,
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-neutral-100)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '1px solid var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-xs)'
  },
  actions: {
    display: 'flex',
    gap: 'var(--padding-sm)',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'var(--padding-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    borderTop: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    position: 'sticky',
    bottom: 0
  },
  dirtyIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)'
  },
  dirtyDot: {
    width: 8,
    height: 8,
    borderRadius: 4
  },
  fileInput: { display: 'none' },
  emptyState: {
    padding: 'var(--padding-xl)',
    textAlign: 'center',
    color: 'var(--color-neutral-500)',
    border: '1px dashed var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)'
  }
}))

function SourceBadge ({ source, classes }) {
  const label = source === 'firm' ? 'Firm' : source === 'user' ? 'User' : 'Default'
  const style = source === 'firm'
    ? classes.sourceFirm
    : source === 'user' ? classes.sourceUser : classes.sourceDefault
  return <Chip size='small' label={label} className={`${classes.sourceChip} ${style}`} />
}
SourceBadge.propTypes = { source: PropTypes.string, classes: PropTypes.object.isRequired }

function readFileAsJpegBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') return reject(new Error('Unexpected file read result'))
      const commaIdx = result.indexOf(',')
      if (commaIdx < 0) return reject(new Error('Unable to parse data URL'))
      resolve(result.slice(commaIdx + 1))
    }
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Seed form state from the effective-config response so what the admin sees
 * in the form matches what meetings will actually use. The sources map on
 * the data drives the per-field Default/Firm/User attribution chips.
 */
/* denali.aiPreferences is shared with calc-api which stores values as
   `{ value, ... }` envelopes. The core-data read path unwraps these, but
   we coerce defensively here too so that: (a) the UI is resilient to raw
   envelope leaks, and (b) any non-string fallthrough can't crash the
   `.trim()` consumers below. */
function asString (v) {
  if (typeof v === 'string') return v
  if (v == null) return ''
  if (typeof v === 'object' && 'value' in v) return asString(v.value)
  return ''
}
function asBool (v, fallback) {
  if (typeof v === 'boolean') return v
  if (v && typeof v === 'object' && 'value' in v) return asBool(v.value, fallback)
  return fallback
}

function seedForm (data) {
  const eff = data?.effective || {}
  return {
    botName: asString(eff.botName),
    disclosureEnabled: asBool(eff.disclosureEnabled, false),
    disclosureText: asString(eff.disclosureText),
    onBotJoinEnabled: asBool(eff.chat?.onBotJoin?.enabled, true),
    onBotJoinMessage: asString(eff.chat?.onBotJoin?.message),
    onPartJoinEnabled: asBool(eff.chat?.onParticipantJoin?.enabled, true),
    onPartJoinMessage: asString(eff.chat?.onParticipantJoin?.message),
    onPartJoinExcludeHost: asBool(eff.chat?.onParticipantJoin?.excludeHost, true)
  }
}

/** Debounced user search for the User tab, matching the pattern used in
 *  DenaliAI/Skills/SkillWorkspace/SkillConfigPopover for consistency. */
function useUserSearch () {
  const [input, setInput] = useState('')
  const [debounced, setDebounced] = useState('')
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const setSearch = useCallback((val) => {
    setInput(val)
    clearTimeout(timerRef.current)
    if (val.length < 2) { setDebounced(''); return }
    timerRef.current = setTimeout(() => setDebounced(val), 350)
  }, [])

  const { data, error } = useQuery({
    queryKey: ['meeting-user-search', debounced],
    queryFn: async () => {
      const { data: res } = await fetchUsers({
        searchFields: ['firstName', 'lastName', 'email'],
        searchText: debounced,
        limit: 20,
        offset: 0
      })
      return res
    },
    enabled: debounced.length >= 2
  })

  const options = useMemo(() => (data?.users || []).map(u => {
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
    return { userId: u.internalId, label: name ? `${name} (${u.internalId})` : `User ${u.internalId}`, email: u.email }
  }), [data])

  return { input, setSearch, options, error }
}

function MeetingsWorkspace () {
  const classes = useStyles()
  const history = useHistory()
  const fileInputRef = useRef(null)

  const [tab, setTab] = useState('firm')
  const [selectedUser, setSelectedUser] = useState(null)
  const userSearch = useUserSearch()

  const [toast, setToast] = useState(null)

  /* Surface user-search failures so a sustained outage doesn't look like
     "no users found anywhere". */
  useEffect(() => {
    if (userSearch.error) {
      setToast({ severity: 'error', message: 'User search failed. Try again in a moment.' })
    }
  }, [userSearch.error])

  /* firmId is scoped server-side by middleware, so we only opt-in to the
     USER level by passing userId. */
  const queryUserId = tab === 'user' ? selectedUser?.userId : undefined
  const queryEnabled = tab === 'firm' || !!selectedUser?.userId

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useGetMeetingConfigEffective({
    userId: queryUserId,
    includeAvatarBytes: true,
    enabled: queryEnabled
  })
  const setConfig = useAdminSetMeetingConfig()

  const [form, setForm] = useState(seedForm(null))
  const [initial, setInitial] = useState(seedForm(null))
  const [pendingAvatar, setPendingAvatar] = useState(null)
  const [clearAvatar, setClearAvatar] = useState(false)

  /* Seed the form from server data only when the payload identity changes
     (tab/user switch, refetch after save). Seeding on every `data` reference
     would stomp user keystrokes because useQuery may hand back a new object
     on background refetches — the symptom there is that the input appears
     uneditable.

     Identity is "tab + userId + dataUpdatedAt". Earlier versions keyed on
     `composedBotName`, which silently broke post-save reseeding for any
     change that didn't affect the composed name (avatar replace, chat
     toggles, excludeHost flips). `dataUpdatedAt` always advances on a real
     refetch, so a successful save followed by `refetch()` reliably
     re-seeds. */
  const seedKey = `${tab}:${data?.userId ?? 'firm'}:${dataUpdatedAt}`
  const lastSeedKeyRef = useRef(null)
  const reseedFromData = useCallback((fresh) => {
    if (!fresh) return
    const seeded = seedForm(fresh)
    setForm(seeded)
    setInitial(seeded)
    setPendingAvatar(null)
    setClearAvatar(false)
  }, [])
  useEffect(() => {
    if (!data) return
    if (lastSeedKeyRef.current === seedKey) return
    lastSeedKeyRef.current = seedKey
    reseedFromData(data)
  }, [data, seedKey, reseedFromData])

  /* Field onChange handlers. CRITICAL: DO NOT inline these as
     `onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))}`. This
     codebase is React 16 which pools SyntheticEvent instances — by the time
     React runs the functional state updater, it has already called
     `event.destructor()` and nulled out `e.target`. Every keystroke then
     resolves to `e.target?.value ?? ''` = '' and writes empty string back to
     form state, so the input appears frozen. Capturing the value outside the
     updater is what makes typing work. */
  const handleBotNameChange = useCallback((e) => {
    const v = e.target.value
    setForm(f => ({ ...f, botName: v }))
  }, [])
  const handleDisclosureEnabledChange = useCallback((e) => {
    const v = e.target.checked
    setForm(f => ({ ...f, disclosureEnabled: v }))
  }, [])
  const handleDisclosureTextChange = useCallback((e) => {
    const v = e.target.value
    setForm(f => ({ ...f, disclosureText: v }))
  }, [])
  const handleOnBotJoinEnabledChange = useCallback((e) => {
    const v = e.target.checked
    setForm(f => ({ ...f, onBotJoinEnabled: v }))
  }, [])
  const handleOnBotJoinMessageChange = useCallback((e) => {
    const v = e.target.value
    setForm(f => ({ ...f, onBotJoinMessage: v }))
  }, [])
  const handleOnPartJoinEnabledChange = useCallback((e) => {
    const v = e.target.checked
    setForm(f => ({ ...f, onPartJoinEnabled: v }))
  }, [])
  const handleOnPartJoinMessageChange = useCallback((e) => {
    const v = e.target.value
    setForm(f => ({ ...f, onPartJoinMessage: v }))
  }, [])
  const handleOnPartJoinExcludeHostChange = useCallback((e) => {
    const v = e.target.checked
    setForm(f => ({ ...f, onPartJoinExcludeHost: v }))
  }, [])

  /* Warn before discarding unsaved changes when switching tabs or picking a
     different user — the router `Prompt` above only catches route-level
     navigation. `isFormDirty` is declared further down, so we mirror it
     onto a ref to avoid a TDZ reference here. */
  const isFormDirtyRef = useRef(false)
  const confirmDiscardIfDirty = useCallback((message) => {
    if (!isFormDirtyRef.current) return true
    // eslint-disable-next-line no-alert
    return window.confirm(message)
  }, [])
  const handleTabChange = useCallback((_e, nextTab) => {
    if (nextTab === tab) return
    if (!confirmDiscardIfDirty('You have unsaved changes. Discard them and switch tabs?')) return
    setTab(nextTab)
    setSelectedUser(null)
    const blank = seedForm(null)
    setForm(blank)
    setInitial(blank)
    setPendingAvatar(null)
    setClearAvatar(false)
  }, [tab, confirmDiscardIfDirty])
  const handleSelectedUserChange = useCallback((_e, val) => {
    const next = val || null
    if ((next?.userId ?? null) === (selectedUser?.userId ?? null)) return
    if (!confirmDiscardIfDirty('You have unsaved changes. Discard them and pick a different user?')) return
    setSelectedUser(next)
  }, [selectedUser, confirmDiscardIfDirty])

  const sources = data?.sources || {}

  const existingAvatarDataUrl = useMemo(() => {
    const a = data?.avatarPreview
    if (!a?.base64) return null
    return `data:${a.mimeType};base64,${a.base64}`
  }, [data?.avatarPreview])

  const displayedAvatarDataUrl = useMemo(() => {
    if (clearAvatar) return null
    if (pendingAvatar) return `data:${pendingAvatar.mimeType};base64,${pendingAvatar.base64}`
    return existingAvatarDataUrl
  }, [clearAvatar, pendingAvatar, existingAvatarDataUrl])

  const composedPreviewName = useMemo(() => {
    const name = (asString(form.botName).trim() || 'Denali Notetaker')
    if (!form.disclosureEnabled) return name.slice(0, MAX_BOT_NAME)
    const text = asString(form.disclosureText).trim()
    if (!text) return name.slice(0, MAX_BOT_NAME)
    const combined = `${name} ${text}`
    if (combined.length <= MAX_BOT_NAME) return combined
    const room = MAX_BOT_NAME - text.length - 1
    if (room <= 0) return text.slice(0, MAX_BOT_NAME)
    return `${name.slice(0, room).trimEnd()} ${text}`
  }, [form.botName, form.disclosureEnabled, form.disclosureText])

  const isFormDirty = useMemo(() => {
    if (pendingAvatar || clearAvatar) return true
    return Object.keys(form).some(k => form[k] !== initial[k])
  }, [form, initial, pendingAvatar, clearAvatar])
  isFormDirtyRef.current = isFormDirty

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'image/jpeg') {
      setToast({ severity: 'error', message: 'Avatar must be a JPEG image.' })
      e.target.value = ''
      return
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setToast({ severity: 'error', message: `Avatar must be ${Math.round(MAX_AVATAR_BYTES / 1000)} KB or smaller.` })
      e.target.value = ''
      return
    }
    try {
      const base64 = await readFileAsJpegBase64(file)
      setPendingAvatar({ base64, mimeType: 'image/jpeg', byteSize: file.size, fileName: file.name })
      setClearAvatar(false)
    } catch (err) {
      setToast({ severity: 'error', message: err.message || 'Failed to read image.' })
    } finally {
      e.target.value = ''
    }
  }, [])

  /**
   * Build the sparse changes payload. A field contributes iff it differs
   * from its seeded (initial) value, which came from the effective merge
   * seen on load. This means editing a field that was inherited-from-firm
   * will pin the new value as a firm/user override — i.e. the backend
   * treats a value write as "override at the level being edited".
   *
   * A future iteration will add a "Clear override" affordance (emit null)
   * so an admin can drop an override to re-inherit from the parent level
   * without needing to know the parent's current value.
   */
  const handleSave = useCallback(async () => {
    if (tab === 'user' && !selectedUser?.userId) {
      setToast({ severity: 'error', message: 'Select a user first.' })
      return
    }

    const changes = {}
    const trimmedName = asString(form.botName).trim()
    if (trimmedName !== asString(initial.botName).trim()) {
      changes[KEY.NAME] = trimmedName || null
    }
    if (form.disclosureEnabled !== initial.disclosureEnabled) {
      changes[KEY.DISCLOSURE_ENABLED] = form.disclosureEnabled
    }
    const trimmedDisclosure = asString(form.disclosureText).trim()
    if (trimmedDisclosure !== asString(initial.disclosureText).trim()) {
      changes[KEY.DISCLOSURE_TEXT] = trimmedDisclosure
    }
    if (form.onBotJoinEnabled !== initial.onBotJoinEnabled) {
      changes[KEY.ON_BOT_JOIN_ENABLED] = form.onBotJoinEnabled
    }
    const trimmedBotMsg = asString(form.onBotJoinMessage).trim()
    if (trimmedBotMsg !== asString(initial.onBotJoinMessage).trim()) {
      if (!trimmedBotMsg) {
        setToast({ severity: 'error', message: 'On-join message cannot be empty.' })
        return
      }
      changes[KEY.ON_BOT_JOIN_MESSAGE] = trimmedBotMsg
    }
    if (form.onPartJoinEnabled !== initial.onPartJoinEnabled) {
      changes[KEY.ON_PART_JOIN_ENABLED] = form.onPartJoinEnabled
    }
    const trimmedPartMsg = asString(form.onPartJoinMessage).trim()
    if (trimmedPartMsg !== asString(initial.onPartJoinMessage).trim()) {
      if (!trimmedPartMsg) {
        setToast({ severity: 'error', message: 'On-participant-join message cannot be empty.' })
        return
      }
      changes[KEY.ON_PART_JOIN_MESSAGE] = trimmedPartMsg
    }
    if (form.onPartJoinExcludeHost !== initial.onPartJoinExcludeHost) {
      changes[KEY.ON_PART_JOIN_EXCLUDE_HOST] = form.onPartJoinExcludeHost
    }

    const payload = {
      changes,
      clearAvatar: clearAvatar || false
    }
    if (tab === 'user') payload.userId = selectedUser.userId
    if (pendingAvatar && !clearAvatar) {
      payload.avatarUpload = {
        base64: pendingAvatar.base64,
        mimeType: pendingAvatar.mimeType
      }
    }

    const hasWork = Object.keys(changes).length > 0 || !!payload.avatarUpload || payload.clearAvatar
    if (!hasWork) {
      setToast({ severity: 'info', message: 'No changes to save.' })
      return
    }

    try {
      await setConfig.mutateAsync(payload)
      await refetch()
      setToast({ severity: 'success', message: 'Configuration saved.' })
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save configuration'
      setToast({ severity: 'error', message })
    }
  }, [tab, selectedUser, form, initial, pendingAvatar, clearAvatar, refetch, setConfig])

  const renderEditor = () => {
    if (tab === 'user' && !selectedUser) {
      return (
        <div className={classes.emptyState}>
          Pick a user above to view or override their meeting configuration.
        </div>
      )
    }
    if (isLoading) return <Loading />
    if (isError) {
      return (
        <Alert severity='error'>
          Failed to load configuration: {error?.message || 'unknown error'}
        </Alert>
      )
    }
    if (!data) return null

    return (
      <>
        <div className={classes.card}>
          <div className={classes.cardHeading}>Display</div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>Display name</span>
              <SourceBadge source={sources[KEY.NAME]?.source} classes={classes} />
            </div>
            <SydLabel>
              <SydInput
                value={asString(form.botName)}
                onChange={handleBotNameChange}
                inputProps={{ maxLength: MAX_BOT_NAME }}
                placeholder='Denali Notetaker'
              />
            </SydLabel>
            <div className={classes.hint}>
              Max {MAX_BOT_NAME} chars. Shown as the display name in Zoom / Meet / Teams.
            </div>
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>Compliance indicator</span>
              <SourceBadge source={sources[KEY.DISCLOSURE_ENABLED]?.source} classes={classes} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.disclosureEnabled}
                  onChange={handleDisclosureEnabledChange}
                  size='small'
                />
              }
              label='Append a recording disclosure to the display name'
            />
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>Compliance indicator text</span>
              <SourceBadge source={sources[KEY.DISCLOSURE_TEXT]?.source} classes={classes} />
            </div>
            <SydLabel>
              <SydInput
                value={asString(form.disclosureText)}
                onChange={handleDisclosureTextChange}
                inputProps={{ maxLength: MAX_DISCLOSURE }}
                placeholder='(Recording)'
                disabled={!form.disclosureEnabled}
              />
            </SydLabel>
            <div className={classes.hint}>
              Preview: <strong>{composedPreviewName}</strong>
            </div>
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>Avatar</span>
              <SourceBadge source={sources[KEY.AVATAR]?.source} classes={classes} />
            </div>
            <div className={classes.avatarRow}>
              <div
                className={classes.avatarPreview}
                style={displayedAvatarDataUrl ? { backgroundImage: `url(${displayedAvatarDataUrl})` } : undefined}
              >
                {displayedAvatarDataUrl ? '' : 'No avatar'}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/jpeg'
                  className={classes.fileInput}
                  onChange={handleFileChange}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <SydButton size='sm' onClick={() => fileInputRef.current?.click()}>
                    {displayedAvatarDataUrl ? 'Replace' : 'Upload'}
                  </SydButton>
                  {(existingAvatarDataUrl || pendingAvatar) && (
                    <SydButton
                      size='sm'
                      variant='secondary'
                      onClick={() => { setPendingAvatar(null); setClearAvatar(true) }}
                    >
                      Clear
                    </SydButton>
                  )}
                </div>
                <div className={classes.hint}>
                  JPEG only, up to {Math.round(MAX_AVATAR_BYTES / 1000)} KB. Shown as the video tile in Zoom / Meet / Teams.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeading}>Chat announcements</div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>On join</span>
              <SourceBadge source={sources[KEY.ON_BOT_JOIN_ENABLED]?.source} classes={classes} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.onBotJoinEnabled}
                  onChange={handleOnBotJoinEnabledChange}
                  size='small'
                />
              }
              label='Post an announcement in the meeting chat on join'
            />
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>On-join message</span>
              <SourceBadge source={sources[KEY.ON_BOT_JOIN_MESSAGE]?.source} classes={classes} />
            </div>
            <textarea
              className={classes.textArea}
              value={asString(form.onBotJoinMessage)}
              onChange={handleOnBotJoinMessageChange}
              maxLength={MAX_CHAT_MESSAGE}
              disabled={!form.onBotJoinEnabled}
              placeholder={`Hi, I'm ${BOT_NAME_TOKEN}. This meeting is being recorded and transcribed.`}
            />
            <div className={classes.hint}>
              Use <code>{BOT_NAME_TOKEN}</code> to insert the composed display name. Max {MAX_CHAT_MESSAGE} chars.
            </div>
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>On participant join</span>
              <SourceBadge source={sources[KEY.ON_PART_JOIN_ENABLED]?.source} classes={classes} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.onPartJoinEnabled}
                  onChange={handleOnPartJoinEnabledChange}
                  size='small'
                />
              }
              label='Post a disclosure to each participant as they join'
            />
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>On-participant-join message</span>
              <SourceBadge source={sources[KEY.ON_PART_JOIN_MESSAGE]?.source} classes={classes} />
            </div>
            <textarea
              className={classes.textArea}
              value={asString(form.onPartJoinMessage)}
              onChange={handleOnPartJoinMessageChange}
              maxLength={MAX_CHAT_MESSAGE}
              disabled={!form.onPartJoinEnabled}
              placeholder='Just so you know, this meeting is being recorded and transcribed.'
            />
            <div className={classes.hint}>
              Sent to each participant as they join. Recall does not expose a participant-name token, so the message is static.
            </div>
          </div>

          <div className={classes.fieldRow}>
            <div className={classes.fieldHeader}>
              <span className={classes.fieldLabel}>Skip the host</span>
              <SourceBadge source={sources[KEY.ON_PART_JOIN_EXCLUDE_HOST]?.source} classes={classes} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.onPartJoinExcludeHost}
                  onChange={handleOnPartJoinExcludeHostChange}
                  size='small'
                  disabled={!form.onPartJoinEnabled}
                />
              }
              label="Don't send the per-participant disclosure to the host (they scheduled the meeting)"
            />
          </div>
        </div>

        <div className={classes.actions}>
          <div className={classes.dirtyIndicator}>
            <span
              className={classes.dirtyDot}
              style={{ backgroundColor: isFormDirty ? 'var(--color-warning-400)' : 'var(--color-success-500)' }}
            />
            {isFormDirty ? 'Unsaved changes' : 'Saved'}
          </div>
          <SydButton
            size='sm'
            variant='primary'
            onClick={handleSave}
            processing={setConfig.isLoading}
            disabled={setConfig.isLoading || !isFormDirty}
          >
            Save changes
          </SydButton>
        </div>
      </>
    )
  }

  return (
    <AdminPage>
      <Prompt
        when={isFormDirty}
        message='You have unsaved changes. Are you sure you want to leave?'
      />

      <div className={classes.header}>
        <div>
          <div className={classes.title}>Meetings</div>
          <div className={classes.subtitle}>
            Configure the meeting assistant's display name, avatar, compliance indicator, and chat announcements. Firm values apply to everyone; user-level overrides take precedence for that user's meetings.
          </div>
        </div>
        <SydButton size='sm' variant='secondary' onClick={() => history.goBack()}>
          Back
        </SydButton>
      </div>

      <div className={classes.tabsBar}>
        <Tabs value={tab} onChange={handleTabChange} indicatorColor='primary' textColor='primary'>
          <Tab value='firm' label='Firm' />
          <Tab value='user' label='User' />
        </Tabs>
      </div>

      {tab === 'user' && (
        <div className={classes.userPickerRow}>
          <div style={{ flex: '0 0 360px' }}>
            <SydLabel label='User'>
              <Autocomplete
                size='small'
                options={userSearch.options}
                getOptionLabel={(opt) => opt.label || ''}
                getOptionSelected={(opt, val) => opt.userId === val?.userId}
                inputValue={userSearch.input}
                onInputChange={(_e, val, reason) => {
                  if (reason === 'reset') return
                  userSearch.setSearch(val || '')
                }}
                onChange={handleSelectedUserChange}
                renderInput={(params) => (
                  <TextField {...params} variant='outlined' size='small' placeholder='Min 2 chars…' />
                )}
                value={selectedUser}
                filterOptions={(x) => x}
              />
            </SydLabel>
          </div>
          {selectedUser && (
            <div className={classes.hint} style={{ marginBottom: 8 }}>
              Editing overrides for <strong>{selectedUser.label}</strong>. Unset fields inherit from the firm level.
            </div>
          )}
        </div>
      )}

      {renderEditor()}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast
          ? <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>{toast.message}</Alert>
          : null}
      </Snackbar>
    </AdminPage>
  )
}

export default MeetingsWorkspace
